import { clearMarkFields, MARK_FIELDS } from "./richtext";
import type { SceneElement } from "./schema";

/**
 * „Format übertragen“ (⌥⌘C / ⌥⌘V): the visual style of one element, applied
 * to others of the same type. Content and geometry (text, image, position,
 * size) stay; brand links travel with the values they resolve.
 */

type ElementType = SceneElement["type"];
type BrandSlot = "fill" | "stroke" | "color" | "textStroke" | "text" | "radius";

/** Style properties per element type (besides opacity and shadow). */
const STYLE_FIELDS: Partial<Record<ElementType, readonly string[]>> = {
  text: ["style", "textScale", "effect", "curve"],
  shape: ["fill", "stroke", "radius"],
  image: ["radius", "stroke", "color"],
  qr: ["color", "background", "style"],
  icon: ["color"],
  svg: [],
};

/** Brand links that belong to the style of each type. */
const BRAND_SLOTS: Partial<Record<ElementType, readonly BrandSlot[]>> = {
  text: ["color", "textStroke", "text"],
  shape: ["fill", "stroke", "radius"],
  image: ["stroke", "radius"],
  qr: ["fill", "color"],
  icon: ["color"],
  svg: [],
};

const COMMON_FIELDS = ["opacity", "shadow"] as const;

export interface CopiedStyle {
  type: ElementType;
  /** Values of the style fields; missing optional fields are `undefined`. */
  values: Record<string, unknown>;
  brand: Partial<Record<BrandSlot, string>>;
}

function clone<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

/** Can the style of this element be copied? */
export function canCopyStyle(element: SceneElement | undefined): boolean {
  return Boolean(element && STYLE_FIELDS[element.type]);
}

/** The visual style of an element, or null for types without one (groups, captions). */
export function extractStyle(element: SceneElement): CopiedStyle | null {
  const fields = STYLE_FIELDS[element.type];
  if (!fields) return null;
  const record = element as unknown as Record<string, unknown>;
  const values: Record<string, unknown> = {};
  for (const field of [...COMMON_FIELDS, ...fields]) values[field] = clone(record[field]);
  const brand: CopiedStyle["brand"] = {};
  for (const slot of BRAND_SLOTS[element.type] ?? []) {
    const token = element.brand?.[slot];
    if (token) brand[slot] = token;
  }
  return { type: element.type, values, brand };
}

/** Can a copied style be applied to this element? Only elements of the same type. */
export function canApplyStyle(style: CopiedStyle, element: SceneElement | undefined): boolean {
  return Boolean(element && element.type === style.type && !element.locked);
}

/**
 * Applies a copied style to an element (mutating; for Immer drafts). Text
 * marks lose their formatting fields, so the copied style shows on the whole
 * text; lists and paragraphs stay. Returns false when not compatible.
 */
export function applyStyle(element: SceneElement, style: CopiedStyle): boolean {
  if (!canApplyStyle(style, element)) return false;
  const record = element as unknown as Record<string, unknown>;
  for (const [field, value] of Object.entries(style.values)) {
    if (value === undefined) delete record[field];
    else record[field] = clone(value);
  }
  const slots = BRAND_SLOTS[element.type] ?? [];
  if (slots.length > 0) {
    const brand: Record<string, string | undefined> = { ...(element.brand ?? {}) };
    for (const slot of slots) {
      if (style.brand[slot]) brand[slot] = style.brand[slot];
      else delete brand[slot];
    }
    const kept = Object.fromEntries(Object.entries(brand).filter(([, token]) => token));
    if (Object.keys(kept).length > 0) element.brand = kept as SceneElement["brand"];
    else delete element.brand;
  }
  if (element.type === "text") clearMarkFields(element, MARK_FIELDS);
  return true;
}

/** German name of the element type, for messages. */
export function styleTypeLabel(type: ElementType): string {
  switch (type) {
    case "text":
      return "Texte";
    case "shape":
      return "Formen";
    case "image":
      return "Bilder";
    case "qr":
      return "QR-Codes";
    case "icon":
      return "Icons";
    case "svg":
      return "Grafiken";
    default:
      return "Elemente";
  }
}
